import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { fetchBranches } from "../../apis/branchApi";
import { fetchClassTypes } from "../../apis/classTypeApi";
import { loadEvents } from "../nextSessions/loadNextSessions";
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { Select } from "antd";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('es');

const ClassDetails = forwardRef(({ formData, setFormData }, ref) => {
    const [branches, setBranches] = useState([]);
    const fromDate = dayjs.utc();
    const toDate = dayjs.utc().add(6, 'day');
    const [events, setEvents] = useState([]);
    const [classTypes, setClassTypes] = useState([]);

    const [event, setEvent] = useState(formData.event || null);
    const [formErrors, setFormErrors] = useState({});
    const [classType, setClassType] = useState(formData.classType || null);

    const loadBranches = async () => {
        const branches = await fetchBranches();
        if(branches.length === 1) {
            const loadedEvents = await loadEvents(branches[0].id, fromDate, toDate, true, true);
            setEvents(loadedEvents);
            return;
        }
        setBranches(branches);
        // TODO: Handle case where multiple branches are available
    };

    useEffect(() => {
        (async () => {
            await loadBranches();
        })();
    }, []);

    // Fetch class types when events change
    useEffect(() => {
        const fetchTypes = async () => {
            if (!events.length) {
                setClassTypes([]);
                return;
            }
            // Get unique class_type_ids
            const ids = Array.from(new Set(events.map(ev => ev.class_type_id)));
            const result = await fetchClassTypes(ids);
            if (result.success) {
              setClassTypes(result.data || []);
            } else {
              setClassTypes([]);
            }
        };
        fetchTypes();
    }, [events]);

    useEffect(() => {
        setFormData(prevFormData => ({ ...prevFormData, event, classType }));
    }, [classType, event, setFormData]);

    function validate(){
        const errors = {};
        if (!classType) errors.classType = "Selecciona un tipo de clase";
        if (!event) errors.event = "Selecciona un evento";
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    useImperativeHandle(ref, () => ({
        validate,
    }));

    // Prepare class type options for Select
    const classTypeOptions = classTypes.map(type => ({
        value: type.id,
        label: type.type
    }));

    // Filter events by selected class type (if any)
    const filteredEvents = classType
      ? events.filter(ev => ev.class_type_id === classType)
      : [];

    return (
        <div className="mt-4">
            <div className="m-auto w-100">
                <div className="input-group col-10 col-md-5 p-0 mx-auto justify-content-between mb-3">
                    <Select
                        className="w-100"
                        placeholder="Selecciona un tipo de clase"
                        value={classType}
                        onChange={value => {
                          setClassType(value);
                          setEvent(null);
                          setFormErrors(prevErrors => ({ ...prevErrors, event: null, classType: null }));
                        }}
                        options={classTypeOptions}
                        allowClear
                    />
                    {formErrors.classType && <div style={{ color: 'red' }}>{formErrors.classType}</div>}
                </div>
                <div className="input-group col-10 col-md-5 p-0 mx-auto justify-content-between">
                    <Select
                        className="w-100"
                        placeholder="Selecciona un día"
                        value={event ? `${event.event_id}_${event.start_date}` : undefined}
                        onChange={value => {
                            const [eventId, startDate] = value.split('_');
                            const selectedEvent = filteredEvents.find(
                                ev => String(ev.event_id) === eventId && String(ev.start_date) === startDate
                            );
                            setEvent(selectedEvent);
                            setFormErrors(prevErrors => ({ ...prevErrors, event: null }));
                        }}
                        options={filteredEvents.map(ev => ({
                            value: `${ev.event_id}_${ev.start_date}`,
                            label: `${dayjs.utc(ev.start_date).tz(dayjs.tz.guess()).format('dddd D MMMM HH:mm').replace(/^./, str => str.toUpperCase())}`,
                        }))}
                        disabled={!classType}
                    />
                    {formErrors.event && <div style={{ color: 'red' }}>{formErrors.event}</div>}
                </div>
            </div>
        </div>
    );
});

export default ClassDetails;